import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlanFeatures } from '../hooks/useStoreData';
import { isFeatureEnabled, getFeatureLimit, PlanFeatures } from '../types/planFeatures';
import {
  HomeIcon,
  UsersIcon,
  MapIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  NewspaperIcon,
  BuildingStorefrontIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, backendUser, logout } = useAuth();
  const location = useLocation();
  const storeId = backendUser?.storeId || null;
  const { planFeatures } = usePlanFeatures(storeId);
  const userRole = backendUser?.role || 'USER';

  // Helper function to check plan feature
  const hasFeature = (feature: keyof PlanFeatures): boolean => {
    return isFeatureEnabled(planFeatures, feature);
  };

  const getLimit = (feature: keyof PlanFeatures): number => {
    return getFeatureLimit(planFeatures, feature);
  };

  // Build navigation based on plan features
  const navigation: NavItem[] = useMemo(() => {
    const menus: NavItem[] = [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ];

    // Users menu - only for system admins
    if (userRole === 'SYSTEM_ADMIN' || userRole === 'SUPERADMIN') {
      menus.push({
        name: 'Users',
        href: '/users/riders',
        icon: UsersIcon,
        children: [
          { name: 'Riders', href: '/users/riders', icon: UsersIcon },
          { name: 'Store Managers', href: '/users/store-managers', icon: UsersIcon },
          { name: 'Club Admins', href: '/users/club-admins', icon: UserGroupIcon }
        ]
      });

      menus.push({ name: 'Stores', href: '/stores', icon: BuildingStorefrontIcon });
      menus.push({ name: 'Clubs', href: '/clubs', icon: UserGroupIcon });
    }

    // Stores menu - for store admins/managers and club admins (their own store/club)
    if (userRole === 'STORE_ADMIN' || userRole === 'STORE_MANAGER' || userRole === 'CLUB_ADMIN') {
      if (storeId) {
        menus.push({ name: 'My Store', href: `/stores/${storeId}`, icon: BuildingStorefrontIcon });
      }
    }

    // Rides - available to all
    menus.push({
      name: 'Rides',
      href: '/rides',
      icon: MapIcon,
      children: [
        { name: 'Templates', href: '/templates', icon: DocumentDuplicateIcon }
      ]
    });

    // News & Offers - hide for Starter plan (feed_posts: 0)
    const feedPosts = getLimit('feed_posts');
    if (feedPosts !== 0 || userRole === 'SYSTEM_ADMIN' || userRole === 'SUPERADMIN') {
      menus.push({ name: 'News & Offers', href: '/news', icon: NewspaperIcon });
    }

    // Marketing - only for Essential/Professional (has emails_per_month), exclude CLUB_ADMIN
    const emailsPerMonth = getLimit('emails_per_month');
    if ((emailsPerMonth > 0 || userRole === 'SYSTEM_ADMIN' || userRole === 'SUPERADMIN') && userRole !== 'CLUB_ADMIN') {
      const marketingChildren: NavItem[] = [
        { name: 'Email Composer', href: '/marketing/email', icon: EnvelopeIcon }
      ];

      // AI Suggestions - only for Professional (ai_suggestions: true)
      if (hasFeature('ai_suggestions') || userRole === 'SYSTEM_ADMIN' || userRole === 'SUPERADMIN') {
        marketingChildren.push({
          name: 'AI Suggestions',
          href: '/marketing/ai-suggestions',
          icon: SparklesIcon
        });
      }

      menus.push({
        name: 'Marketing',
        href: '/marketing',
        icon: MegaphoneIcon,
        children: marketingChildren
      });
    }

    // Analytics - available to all except CLUB_ADMIN
    if (userRole !== 'CLUB_ADMIN') {
      const analyticsChildren: NavItem[] = [
        { name: 'User Garage', href: '/analytics/user-garage', icon: WrenchScrewdriverIcon }
      ];
      
      // Issues - only for Super Admin
      if (userRole === 'SUPERADMIN') {
        analyticsChildren.push({ name: 'Issues', href: '/issues', icon: ExclamationTriangleIcon });
      }
      
      menus.push({
        name: 'Analytics',
        href: '/analytics',
        icon: ChartBarIcon,
        children: analyticsChildren
      });
    }

    // Loyalty - only for Professional (loyalty_points_manager: true)
    if (hasFeature('loyalty_points_manager') || userRole === 'SYSTEM_ADMIN' || userRole === 'SUPERADMIN') {
      menus.push({
        name: 'Loyalty',
        href: '/loyalty',
        icon: TrophyIcon,
        children: [
          { name: 'Points Manager', href: '/loyalty/points-manager', icon: TrophyIcon }
        ]
      });
    }

    // Help section - available to all
    menus.push({
      name: 'Help',
      href: '/help/faq',
      icon: QuestionMarkCircleIcon,
      children: [
        { name: 'FAQ', href: '/help/faq', icon: QuestionMarkCircleIcon },
        { name: 'Contact Support', href: '/help/contact', icon: EnvelopeIcon },
        { name: 'Support Inbox', href: '/help/inbox', icon: ChatBubbleLeftRightIcon }
      ]
    });

    return menus;
  }, [planFeatures, userRole, storeId]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="flex flex-col w-64 bg-white shadow-lg">
        <div className="flex items-center justify-center h-16 bg-primary-600">
          <h1 className="text-xl font-bold text-white">RideConnect Admin</h1>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navigation.map((item) => {
            const isParentActive = location.pathname === item.href;
            const hasActiveChild = item.children && item.children.some(child => location.pathname === child.href);
            const isParentHighlighted = isParentActive || hasActiveChild;
            
            return (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isParentHighlighted
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
                {item.children && (
                  <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.href;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isChildActive
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <child.icon className="w-4 h-4 mr-2" />
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {user?.email}
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
